/**
 * tutorialUrlSync — keep the address bar holding a shareable deep link to the
 * tutorial that is currently on screen.
 *
 * `?tutorialModule=<id>` has always been *readable* at boot (see `init.tsx`),
 * but nothing wrote it, so a tutorial launched from the catalogue produced no
 * copyable URL. This module writes it on every module start and removes it when
 * the tutorial ends, via `history.replaceState` (no new history entries — the
 * back button keeps its existing meaning).
 *
 * The write also normalises the params that would otherwise stop the copied
 * link from reopening the tutorial:
 *   - `noTutorial` is dropped (it suppresses the tutorial outright).
 *   - `tutorial=1` is added *only* when a deep-linked system (`load`/`molxyz`/
 *     `smiles`) is still in the URL, since those imply `noTutorial` unless
 *     explicitly overridden. The link then reproduces what's on screen: the
 *     deep-linked system with the tutorial running on top.
 *
 * Mirrors the shape of `loader/sandboxUrlSync.ts` (pure search-string
 * transform + thin `history` writer) so both URL writers stay comparable. They
 * are independent: each reads the live `location.search` at write time, so the
 * order in which they fire doesn't matter.
 *
 * @module tutorial/tutorialUrlSync
 */

/**
 * Message the embedded playground posts to its landing-page parent so the
 * module id lands in the *visible* address bar. Mirrors `MOLXYZ_MESSAGE_TYPE`.
 */
export const TUTORIAL_MODULE_MESSAGE_TYPE = 'mlipview:tutorialModule';

/** Suppresses the tutorial; removed whenever we write a module id. */
const NO_TUTORIAL_PARAM = 'noTutorial';

/** Explicit "keep the tutorial on top of a deep link" override. */
const TUTORIAL_OVERRIDE_PARAM = 'tutorial';

/** Deep-linked system sources — each implies `noTutorial` on its own. */
const DEEP_LINK_PARAMS = ['load', 'molxyz', 'smiles'] as const;

/**
 * Apply (or remove) the `tutorialModule` value to a `location.search` string,
 * normalising the tutorial-suppressing params along with it. Pure.
 *
 * @param search - current search string, with or without the leading `?`
 * @param moduleId - module id to link to, or `null` to remove the link
 * @returns the new search string (`''` when no params remain)
 */
export function applyTutorialModuleToSearch(search: string, moduleId: string | null): string {
  const params = new URLSearchParams(search || '');
  if (moduleId) {
    params.set('tutorialModule', moduleId);
    params.delete(NO_TUTORIAL_PARAM);
    // A deep-linked system implies noTutorial, so the link needs the explicit
    // override to reopen the tutorial on top of it.
    if (DEEP_LINK_PARAMS.some((p) => params.has(p))) {
      params.set(TUTORIAL_OVERRIDE_PARAM, '1');
    }
  } else {
    params.delete('tutorialModule');
    params.delete(TUTORIAL_OVERRIDE_PARAM);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Write the active tutorial module into the address bar (or remove it when
 * `null`). No-ops when there is nothing to change or when
 * `history.replaceState` is unavailable.
 *
 * Embedded playground (landing-page iframe): the visible URL is the parent's,
 * so post the value up for the landing page to reflect instead of rewriting our
 * own hidden iframe URL. The landing page forwards every query param into the
 * iframe src, so the resulting link reopens the tutorial.
 */
export function syncTutorialModuleUrl(moduleId: string | null): void {
  if (typeof window === 'undefined') return;
  if (window.parent && window.parent !== window) {
    try {
      window.parent.postMessage(
        { type: TUTORIAL_MODULE_MESSAGE_TYPE, value: moduleId },
        window.location.origin,
      );
    } catch {
      /* cross-origin parent — nothing we can do */
    }
    return;
  }
  if (!window.history?.replaceState) return;
  try {
    const next = applyTutorialModuleToSearch(window.location.search, moduleId);
    if (next === window.location.search) return;
    window.history.replaceState(null, '', `${window.location.pathname}${next}${window.location.hash}`);
  } catch {
    /* URL write is cosmetic — never let it break the tutorial */
  }
}
